<script setup lang="ts">
/**
 * Экран «Собрать набор» — оверлей ВНУТРИ модалки товара, как экраны выбора у комбо.
 *
 * Раньше это была вторая модалка поверх первой: своя подложка, свой скролл-лок и потолок
 * высоты в 57dvh на десктопе — набор из четырёх шагов в него не помещался. Теперь экран
 * подменяет содержимое модалки: на мобильном занимает её целиком, на десктопе ложится
 * на колонку медиа (662px), а карточка товара справа остаётся на виду — видно, к чему
 * собирается набор.
 *
 * Отсюда же требования к разметке: родитель (`.more-about-dish`) должен быть `relative`,
 * а сам экран — `absolute inset-0`, иначе на мобильном он не дотянется до низа экрана.
 */
import consola from 'consola';
import { ref, computed, watch } from 'vue';
import BundleBuilderHero from './BundleBuilderHero.vue';
import BundleBuilderStepIndicator from './BundleBuilderStepIndicator.vue';
import BundleBuilderStepContent from './BundleBuilderStepContent.vue';
import BundleBuilderActions from './BundleBuilderActions.vue';
import { useBundleBuilder } from '@features/bundle';
import { menuUnavailableLabel, menuUnavailableHint } from '@entities/product';
import { useCartStore } from '@entities/order';
import { useModalStore, modalStateEnum } from '@shared/model';
import type { ProductBundle, BundleStepProduct } from '@features/bundle';
import { useAuthState } from '~/entities/auth';
import { useKitToast } from '@shared/ui/kit';

import apiBundleInstance from '~/features/bundle/api';
import { useBottomSheet, setAuthEntryPoint } from '~/shared/lib';

const props = defineProps<{
  /** v-model: открыт/закрыт */
  modelValue: boolean;
  /** Бандл, который нужно собрать */
  bundle: ProductBundle | null;
  /** ID базового продукта (бургер, ролл...) */
  baseProductId: number;
  baseProductName: string;
  /** Картинка основного продукта для индикатора шагов */
  baseProductImg: string | null;
  /** Placeholder */
  placeholderImg?: string | null;
  /** ID филиала */
  branchId: string | null;
  /**
   * Состав основного блюда, выбранный в карточке товара. Гость настраивает размер и
   * добавки, а потом собирает набор — и ждёт, что набор соберётся именно с ними.
   */
  baseAddons?: (string | number)[];
  baseExcludes?: (string | number)[];
  /**
   * Доплата за добавки основного блюда. Она уходит в корзину вместе с набором,
   * поэтому обязана быть и в цене на кнопке.
   */
  baseAddonsSum?: number;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const cartStore = useCartStore();
const storeModal = useModalStore();
const { isLoggedIn } = useAuthState();
const { toast } = useKitToast();
const isAddingToCart = ref(false);

/** Тост «нужен вход» с кнопкой «Войти» — набор уходит на сервер и требует авторизации. */
const promptLogin = () => {
  toast({
    color: 'warning',
    title: 'Войдите, чтобы добавить набор',
    description: 'Наборы «Собери набор» сохраняются в корзине только у авторизованных пользователей.',
    timeout: 6000,
    action: {
      label: 'Войти',
      onClick: () => {
        handleClose();
        setAuthEntryPoint('bundle');
        storeModal.open(modalStateEnum.Login);
      },
    },
  });
};

// Скролл фона лочит модалка товара, поверх которой мы лежим: второй лок компенсировал бы
// ширину скроллбара дважды и дёргал вёрстку.

const {
  currentStepIndex,
  selections,
  currentStep,
  calculatedPrice,
  canProceed,
  isLastStep,
  isBundleAvailable,
  blockedStep,
  formatPriceDelta,
  openBundle,
  close,
  selectProduct,
  goNext,
  goBack,
  goToStep,
  getCartPayload,
} = useBundleBuilder(() => props.baseAddonsSum ?? 0);

// При открытии — инициализируем бандл
// immediate: true нужен, чтобы openBundle сработал при первом рендере,
// когда modelValue уже true в момент монтирования компонента
watch(
  () => props.modelValue,
  (opened) => {
    if (opened && props.bundle) {
      openBundle(props.bundle);
    } else if (!opened) {
      close();
    }
  },
  { immediate: true },
);

const handleClose = () => {
  close();
  emit('update:modelValue', false);
};

/**
 * Набор принадлежит КОНКРЕТНОЙ карточке товара: «Сырная куриная» (стандартная) и
 * «Сырная курица» (большая) — разные товары со своими наборами. Если сменить размер, пока
 * экран открыт, собранный набор остался бы от прежней карточки, и в корзину уехал бы не тот
 * товар. Поэтому при смене товара экран закрываем — так же ведёт себя конструктор комбо.
 */
watch(
  () => props.baseProductId,
  (id, previousId) => {
    if (previousId !== undefined && id !== previousId) handleClose();
  },
);

/** Кнопка «назад»: сначала по шагам, с первого — обратно к товару. */
const handleBack = () => {
  if (currentStepIndex.value > 0) {
    goBack();
    return;
  }
  handleClose();
};

/** Свайп вниз для закрытия — только мобиля (как ProductInfoModal + useBottomSheet) */
const {
  sheetRef,
  scrollInnerRef,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
} = useBottomSheet({
  onClose: handleClose,
});

const isMobileSwipe = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches;

const onSheetTouchStart = (e: TouchEvent) => {
  if (!isMobileSwipe()) return;
  handleTouchStart(e);
};
const onSheetTouchMove = (e: TouchEvent) => {
  if (!isMobileSwipe()) return;
  handleTouchMove(e);
};
const onSheetTouchEnd = () => {
  if (!isMobileSwipe()) return;
  handleTouchEnd();
};

const handleSelect = (productId: number) => {
  if (!currentStep.value) return;
  selectProduct(currentStep.value.id, productId);
};

/**
 * Гость ткнул в товар с красной рамкой. Молча игнорировать клик нельзя — это читается
 * как поломка сайта, поэтому объясняем причину тем же тостом, что и остальные отказы.
 */
const handleUnavailableSelect = (product: BundleStepProduct) => {
  toast({
    color: 'warning',
    title: `«${product.name}» — ${menuUnavailableLabel(product).toLowerCase()}`,
    description: menuUnavailableHint(product),
    timeout: 5000,
  });
};

const handleAddToCart = async () => {
  if (!props.branchId) return;

  // Состав мог протухнуть, пока гость собирал набор (стоп-лист приезжает с кассы).
  if (!isBundleAvailable.value) {
    toast({
      color: 'warning',
      title: 'В наборе есть недоступный товар',
      description: 'Замените отмеченные красным позиции — сейчас их не приготовят.',
      timeout: 5000,
    });
    return;
  }
  const payload = getCartPayload(Number(props.branchId), props.baseProductId, {
    addons: props.baseAddons,
    excludes: props.baseExcludes,
  });
  if (!payload) return;

  // Набор добавляется только на сервере (гостевой корзины для наборов нет) —
  // без входа сервер вернёт 401. Предупреждаем заранее, не отправляя обречённый запрос.
  if (!isLoggedIn.value) {
    promptLogin();
    return;
  }

  isAddingToCart.value = true;
  try {
    const response = await apiBundleInstance.addBundleToCart(payload);
    if (response?.cart) {
      await cartStore.updateCartFromServerResponse(response.cart);
    }
    handleClose();
    storeModal.close();
  } catch (error: any) {
    consola.error('[BundleBuilderOverlay] addBundleToCart error:', error);

    // Токен мог протухнуть между рендером и отправкой — ловим 401 как фолбэк.
    const status = error?.status ?? error?.statusCode ?? error?.response?.status;
    // Причину отказа сервер пишет по-человечески («Кола сейчас в стоп-листе») —
    // показываем её как есть, обобщённый текст тут ничего гостю не объясняет.
    const serverMessage: string | undefined =
      error?.response?._data?.message ?? error?.data?.message;

    if (status === 401) {
      promptLogin();
    } else if (status === 400 && serverMessage) {
      toast({
        color: 'warning',
        title: 'Набор не добавлен',
        description: serverMessage,
        timeout: 6000,
      });
    } else {
      toast({
        color: 'danger',
        title: 'Не удалось добавить набор',
        description: 'Попробуйте ещё раз. Если ошибка повторяется — обновите страницу.',
        timeout: 5000,
      });
    }
  } finally {
    isAddingToCart.value = false;
  }
};

// Название набора для заголовка
const stepTitle = computed<string>(() => props.bundle?.name.toUpperCase() ?? '');
</script>

<template>
  <Transition name="bundle-overlay">
    <div
      v-if="modelValue && bundle"
      ref="sheetRef"
      class="bundle-overlay"
      data-testid="bundle-builder"
      @touchstart="onSheetTouchStart"
      @touchmove="onSheetTouchMove"
      @touchend="onSheetTouchEnd"
    >
      <div class="bundle-overlay__header">
        <button
          type="button"
          class="bundle-overlay__icon-btn"
          :aria-label="currentStepIndex > 0 ? 'Назад к предыдущему шагу' : 'Назад к товару'"
          @click="handleBack"
        >
          <UIcon name="i-heroicons-chevron-left" class="w-6 h-6" />
        </button>

        <h3 class="bundle-overlay__title">{{ stepTitle }}</h3>

        <button
          type="button"
          class="bundle-overlay__icon-btn bundle-overlay__icon-btn--close"
          aria-label="Закрыть"
          @click="handleClose"
        >
          <UIcon name="i-heroicons-x-mark" class="w-6 h-6" />
        </button>
      </div>

      <div ref="scrollInnerRef" class="bundle-overlay__scroll">
        <!-- Картинка набора пересобирается на каждом шаге: видно, что уже выбрано.
             Высоту задаёт обёртка: у самого героя свои правила высоты под старую модалку. -->
        <div class="bundle-overlay__hero">
          <BundleBuilderHero
            :hero-image="bundle.hero_image"
            :base-product-img="baseProductImg"
            :steps="bundle.steps"
            :selections="selections"
            :placeholder-img="placeholderImg"
          />
        </div>

        <BundleBuilderStepIndicator
          :bundle="bundle"
          :current-step-index="currentStepIndex"
          :selections="selections"
          :base-product-img="baseProductImg"
          :base-product-name="baseProductName"
          class="bundle-overlay__steps"
          @go-to-step="goToStep"
        />

        <!-- Обязательный шаг, который нечем закрыть: собрать набор нельзя вообще,
             и об этом честнее сказать сразу, а не заблокированной кнопкой внизу. -->
        <div v-if="blockedStep" class="bundle-overlay__notice" data-testid="bundle-blocked-notice">
          <UIcon name="i-heroicons-exclamation-triangle" class="w-5 h-5 shrink-0" />
          <span>
            Набор сейчас недоступен: на шаге «{{ blockedStep.name }}» закончились все варианты.
          </span>
        </div>

        <BundleBuilderStepContent
          v-if="currentStep"
          :step="currentStep"
          :selected-product-id="selections[currentStep.id] ?? null"
          :format-price-delta="formatPriceDelta"
          :placeholder-img="placeholderImg"
          @select="handleSelect"
          @select-unavailable="handleUnavailableSelect"
        />
      </div>

      <div class="bundle-overlay__footer">
        <BundleBuilderActions
          :is-last-step="isLastStep"
          :can-proceed="canProceed && (!isLastStep || isBundleAvailable)"
          :calculated-price="calculatedPrice"
          :is-loading="isAddingToCart"
          @next="goNext"
          @add-to-cart="handleAddToCart"
        />
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
.bundle-overlay {
  @apply absolute inset-0 z-20 flex flex-col min-h-0 overflow-hidden;
  // На десктопе подменяем только колонку медиа — карточка товара справа остаётся на виду
  @apply lg:right-auto lg:w-[662px];

  // Матовое стекло на ::before — на flex-контейнере backdrop-filter ломает
  // расчёт высоты детей в WebKit (см. ComboCustomizePanel).
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    background-color: rgb(18 18 20 / 92%);
    backdrop-filter: blur(28px) saturate(1.4);
    -webkit-backdrop-filter: blur(28px) saturate(1.4);
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  &__header {
    @apply flex items-center gap-2 px-4 pt-4 pb-3 shrink-0;
  }

  &__icon-btn {
    @apply w-9 h-9 rounded-full flex items-center justify-center shrink-0;
    @apply bg-white/10 text-white hover:bg-white/20 transition-colors;
  }

  &__icon-btn--close {
    @apply ml-auto;
  }

  &__title {
    @apply text-base lg:text-xl font-bold text-white leading-tight line-clamp-2;
  }

  &__scroll {
    @apply flex-1 min-h-0 overflow-y-auto px-4 pb-4 flex flex-col gap-4;
  }

  &__hero {
    @apply shrink-0;
    height: 180px;

    @screen lg {
      height: 240px;
    }

    :deep(.bundle-hero) {
      @apply h-full;
      border-radius: 0;
    }
  }

  &__steps {
    @apply shrink-0;
  }

  &__notice {
    @apply shrink-0 flex items-start gap-2 rounded-xl px-3 py-2;
    @apply bg-hui-danger/15 text-hui-danger text-sm font-medium;
  }

  &__footer {
    @apply p-4 shrink-0;
  }
}

.bundle-overlay-enter-active,
.bundle-overlay-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}

.bundle-overlay-enter-from,
.bundle-overlay-leave-to {
  opacity: 0;
  transform: translate3d(0, 1.5rem, 0);
}

@media (prefers-reduced-motion: reduce) {
  .bundle-overlay-enter-active,
  .bundle-overlay-leave-active {
    transition: opacity 0.2s ease;
  }

  .bundle-overlay-enter-from,
  .bundle-overlay-leave-to {
    transform: none;
  }
}
</style>
